import React, { useState, useMemo } from 'react';
import { useQueryClient, useQueries } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Row, Col, Space, Button, Divider } from 'antd';
import { useIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { useLocalStorageState } from 'ahooks';
import { Link } from 'react-router';
import { AccountPersistInfo } from 'Internal.CommonAPI';
import { DateRange, Channel } from 'Common/types';
import { mapRevenueAndProfitReportToChartData } from 'Reports/utils';
import { getSoldProductsCount, getReturnsCount } from 'Reports/selectors';
import {
  useEmailBestSellersReport,
  useEmailTransactionsReport,
} from 'Reports/hooks/dashboard';
import {
  ExportReport,
  ReportType,
} from 'utils/apiClient/services/reports/types';
import {
  mapRevenueByPaymentReportToChartData,
  RevenueByPaymentReportRecord,
} from '../utils';
import MailReportModal from './views/MailReportModal';
import RevenueAndProfitChart from './RevenueOverviewRevenueAndProfitChart';
import RevenueOverviewCardsRow from './views/RevenueOverviewCardsRow';
import RevenueOverviewHeader from './views/RevenueOverviewHeader';
import RevenueOverviewRevenueByPaymentChart from './views/RevenueOverviewRevenueByPaymentChart';
import RevenueOverviewBestsellersTable from './views/RevenueOverviewBestsellersTable';
import ReportCard from './views/ReportCard';
import {
  useOrders,
  useBasic,
  useReturns,
  useBestSellersReport,
  useRevenueByPaymentReport,
} from '../hooks/dashboard';

const ReportCardsWrapper = styled.div`
  padding: 1.5em;
  @media (max-width: 768px) {
    .ant-row > .ant-col {
      margin-bottom: 20px;
    }
  }
`;

const CustomReportCard = styled(ReportCard)`
  @media (max-width: 768px) {
    .ant-row > .ant-col {
      margin-bottom: 10px;
    }
    .ant-card-body {
      column-gap: 10px;
      width: 650px;
    }
  }
`;

const initialFilterState = {
  channel: undefined,
  users: undefined,
};

interface FilterState {
  channel?: Channel;
  users?: number[];
}

type RevenueOverviewProps = {
  dateRange: DateRange;
};
const RevenueOverview: React.FC<RevenueOverviewProps> = ({ dateRange }) => {
  const intl = useIntl();
  const reportType = ReportType.Daily;
  // eslint-disable-next-line
  const [filters, setFilters] = useState<FilterState>(initialFilterState);
  const [isMailReportModalOpen, setIsMailReportOpen] = useState<boolean>(false);
  const [exportReportType, setExportReportType] = useState<string | null>(null);
  const [account] = useLocalStorageState<AccountPersistInfo>('account');
  const queryClient = useQueryClient();
  const cardsQuery = useQueries({
    queries: [
      {
        queryKey: ['reports', 'cards', 'invoices'],
      },
      {
        queryKey: ['reports', 'cards', 'margin'],
      },
      {
        queryKey: ['reports', 'cards', 'totalRevenue'],
      },
      {
        queryKey: ['reports', 'cards', 'avgBasketValue'],
      },
    ],
  });

  const filterOptions = useMemo(
    () => ({ dateRange, type: reportType, ...filters }),
    [filters, reportType, dateRange]
  );
  const revenueByPaymentReport: Array<RevenueByPaymentReportRecord> =
    useRevenueByPaymentReport();

  const revenueByPaymentChartData = useMemo(
    () =>
      revenueByPaymentReport.length > 0
        ? mapRevenueByPaymentReportToChartData(revenueByPaymentReport)
        : [],
    [revenueByPaymentReport]
  );

  const [revenueChartFilterOptions, setRevenueChartFilterOptions] = useState({
    dateRange,
    type: reportType,
  });

  const ordersQuery = useOrders(filterOptions);
  const basicsQuery = useBasic(filterOptions);
  const chartQuery = useBasic({
    ...filterOptions,
    ...(filterOptions.type !== revenueChartFilterOptions.type && {
      type: revenueChartFilterOptions.type,
    }),
  });

  const returnsQuery = useReturns(filterOptions);

  const {
    data: bestsellersReport,
    soldReports,
    loading: productsSoldLoading,
  } = useBestSellersReport(filterOptions);

  const mailBestSellersReportMutation = useEmailBestSellersReport();
  const mailTransactionsReportMutation = useEmailTransactionsReport();

  const reportData = {
    email: account?.email as string,
    end:
      dateRange?.[1]?.format('DD.MM.YYYY') ||
      dayjs().subtract(1, 'week').add(1, 'day').startOf('day').toString(),
    format: 'xls',
    start:
      dateRange?.[0]?.format('DD.MM.YYYY') ||
      dayjs().format('DD.MM.YYYY').toString(),
    type: 'range',
  };

  const openExportModal = (action: string) => {
    setExportReportType(
      action === 'BEST_SELLERS_REPORT'
        ? intl.formatMessage({ defaultMessage: 'Bestseller' })
        : intl.formatMessage({ defaultMessage: 'Transactions' })
    );
    setIsMailReportOpen(true);
  };

  const handleReportType = (input: ReportType) => {
    setRevenueChartFilterOptions({ dateRange, type: input });
    queryClient.setQueryDefaults(['reports', 'cards'], {
      enabled: false,
    });
  };

  const handleEmail = (input: ExportReport) => {
    if (input.reportType === 'Bestseller') {
      mailBestSellersReportMutation.mutate(input);
    } else {
      mailTransactionsReportMutation.mutate(input);
    }
  };

  return (
    <div>
      <RevenueOverviewHeader openModal={openExportModal} />
      <MailReportModal
        header={exportReportType}
        message={reportData}
        visible={isMailReportModalOpen}
        onClose={() => {
          setIsMailReportOpen(false);
        }}
        onSubmit={handleEmail}
      />
      <Divider css="margin: 0" />
      <ReportCardsWrapper>
        <Space direction="vertical" size={25} css="width: 100%">
          <RevenueOverviewCardsRow
            currency={account?.currency as string}
            soldProducts={
              soldReports !== undefined ? getSoldProductsCount(soldReports) : 0
            }
            invoices={
              (queryClient.getQueryData([
                'reports',
                'cards',
                'invoices',
              ]) as number) ?? 0
            }
            returns={
              returnsQuery.data !== undefined
                ? getReturnsCount(returnsQuery.data)
                : 0
            }
            margin={
              (queryClient.getQueryData([
                'reports',
                'cards',
                'margin',
              ]) as number) ?? 0
            }
            totalRevenue={
              (queryClient.getQueryData([
                'reports',
                'cards',
                'totalRevenue',
              ]) as number) ?? 0
            }
            avgBasketValue={
              (queryClient.getQueryData([
                'reports',
                'cards',
                'avgBasketValue',
              ]) as number) ?? 0
            }
            isDataLoading={[
              ordersQuery.isLoading,
              returnsQuery.isLoading,
              basicsQuery.isLoading,
              cardsQuery.some((query) => query.isLoading),
              productsSoldLoading,
            ].find((v) => v)}
          />

          <RevenueAndProfitChart
            options={revenueChartFilterOptions}
            loading={chartQuery.isLoading}
            data={
              // eslint-disable-next-line
              !!chartQuery.data
                ? mapRevenueAndProfitReportToChartData(chartQuery.data)
                : []
            }
            currency={account?.currency as string}
            changeReportType={handleReportType}
          />

          <Row gutter={25}>
            <Col xl={12} md={24}>
              <CustomReportCard
                title={intl.formatMessage({
                  defaultMessage: 'Revenue by payment method',
                })}
              >
                <RevenueOverviewRevenueByPaymentChart
                  isDataLoading={ordersQuery.isLoading}
                  currency={account?.currency as string}
                  data={revenueByPaymentChartData.map((data) => ({
                    ...data,
                    label: data.label
                      ? intl.formatMessage(data.label)
                      : `code: ${data.id}`,
                  }))}
                />
              </CustomReportCard>
            </Col>

            <Col xl={12} md={24}>
              <CustomReportCard
                extra={
                  <Link to="?_=2">
                    <Button type="link">
                      <FormattedMessage defaultMessage="Product statistic" />
                    </Button>
                  </Link>
                }
                bodyStyle={{ padding: 0 }}
                title={intl.formatMessage({
                  defaultMessage: 'Bestseller',
                })}
              >
                <RevenueOverviewBestsellersTable
                  loading={productsSoldLoading}
                  products={bestsellersReport}
                />
              </CustomReportCard>
            </Col>
          </Row>
        </Space>
      </ReportCardsWrapper>
    </div>
  );
};

export default RevenueOverview;
